import { Pin } from 'lucide-react';
import React from 'react';

import { renderTextWithMentions } from '@/components/common/MarkdownText';
import { cn } from '@/lib/utils';

import { usePinnedEventsContext } from './PinnedEventsContext';
import { getEventDefinition } from './registry';
import { MatterLink } from './shared/MatterLink';
import { UserAvatar } from './shared/UserAvatar';
import { DomainEvent } from './types';
import { formatFullDateTime, formatSmartTime } from './utils/formatters';

interface EventItemProps {
  event: DomainEvent;
  isLast: boolean;
  now?: Date;
}

export const EventItem: React.FC<EventItemProps> = React.memo(
  ({ event, isLast, now = new Date() }) => {
    const definition = getEventDefinition(event.event_code);
    const pinnedContext = usePinnedEventsContext();
    const isPinned = pinnedContext?.isPinned(event.id) ?? false;
    const Icon = definition.icon;
    const eventTime = event.occurred_at;

    // Helper function to convert URLs to clickable links AND render mentions
    const renderEventContent = (text: string) => {
      const trimmedText = text.trim();
      if (!trimmedText) return null;

      return (
        <div className="text-xs text-zinc-800 font-normal leading-normal">
          {renderTextWithMentions(trimmedText)}
        </div>
      );
    };

    // Treat staff notes and events with work_log_entry as comment-style
    const isComment =
      event.event_code === 'staff_note_created' ||
      event.event_code === 'task_update_created' ||
      event.event_code === 'treatment_check_in_note_logged' ||
      !!(event.payload as { work_log_entry?: string })?.work_log_entry;

    // Render content (CollapsibleDetails inside render() handle expansion)
    // Always render full content for the body/main area.
    // For comments, we'll manually render the inline/header version separately.
    // Extract the content from staff_note_created events, assuming it's in payload.note.content
    const rawContent =
      (event.event_code === 'staff_note_created' &&
        (event.payload as any)?.note?.content) ||
      (event.event_code === 'task_update_created' &&
        (event.payload as any)?.task_update?.update) ||
      (event.event_code === 'treatment_check_in_note_logged' &&
        (event.payload as any)?.work_log_entry);

    let contentToRender: React.ReactNode;
    if (typeof rawContent === 'string') {
      contentToRender = renderEventContent(rawContent);
    } else {
      contentToRender = definition.render(event, false); // Fallback to original render
    }

    // Safely handle non-string content in the else block for non-comment events too
    // Actually, for non-comment events, we always want to use definition.render
    // But wait, the code below uses renderEventContent(definition.render(event, false)) which is wrong!
    // definition.render returns ReactNode, not string.

    const smartTime = formatSmartTime(eventTime || '', now);
    const fullTime = formatFullDateTime(eventTime || '');

    return (
      <li
        key={event.id}
        className={cn(
          'relative flex gap-x-2 group/item',
          isPinned && 'bg-blue-50/50 -mx-1 px-1 rounded',
        )}
      >
        {/* Timeline connector */}
        <div
          className={cn(
            'absolute left-0 top-0 flex w-5 justify-center',
            isLast ? 'h-5' : '-bottom-3',
            isPinned && 'ml-1',
          )}
        >
          <div className="w-px bg-zinc-200 group-hover/item:bg-zinc-300 transition-colors" />
        </div>

        {isComment ? (
          /* Comment Style Layout */
          <>
            <UserAvatar
              user={event.user}
              size="sm"
              className={cn('relative mt-0.5 flex-none', isPinned && 'ml-1')}
            />
            <div
              className={cn(
                'flex-auto rounded-md px-2 py-1.5 ring-1 ring-inset shadow-sm hover:shadow-md transition-shadow',
                isPinned ? 'ring-blue-200 bg-white' : 'ring-zinc-200 bg-white',
              )}
            >
              <div className="flex justify-between items-baseline gap-x-2">
                <div className="text-[10px] leading-4 text-zinc-500">
                  {/* Inline header for comment blocks */}
                  {definition.render(event, true)}
                </div>
                <div className="flex items-center gap-1">
                  {pinnedContext && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        pinnedContext.togglePin(event.id, event.event_code);
                      }}
                      className={cn(
                        'p-0.5 rounded transition-colors',
                        isPinned
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-zinc-400 hover:text-zinc-600 opacity-0 group-hover/item:opacity-100',
                      )}
                      title={isPinned ? 'Unpin activity' : 'Pin activity'}
                    >
                      <Pin className="h-3 w-3" />
                    </button>
                  )}
                  <time
                    dateTime={eventTime || ''}
                    className="flex-none text-[10px] text-zinc-400"
                    title={fullTime}
                  >
                    {smartTime}
                  </time>
                </div>
              </div>
              {/* Full content - now uses the rich renderer */}
              <div className="mt-0.5">{contentToRender}</div>
              <MatterLink
                matterId={(event as { matter?: string }).matter || ''}
                matterName={
                  (event as { matter_name?: string }).matter_name || ''
                }
                className="mt-1"
              />
            </div>
          </>
        ) : (
          /* Timeline Style Layout */
          <>
            <div
              className={cn(
                'relative flex h-5 w-5 flex-none items-center justify-center bg-white mt-0.5',
                isPinned && 'ml-1',
              )}
            >
              {definition.priority === 'high' ? (
                <div className="relative flex h-5 w-5 items-center justify-center">
                  <div className="absolute inset-0 rounded-full bg-zinc-50 ring-1 ring-zinc-100 group-hover/item:ring-zinc-200 transition-shadow" />
                  <Icon
                    className={cn(
                      definition.iconSize || 'h-3 w-3',
                      definition.iconColor,
                      'relative z-10',
                    )}
                    aria-hidden="true"
                  />
                </div>
              ) : (
                <React.Fragment key={`${event.id}-icon`}>
                  {definition.priority === 'medium' ? (
                    <Icon
                      className={cn(
                        definition.iconSize || 'h-3 w-3',
                        definition.iconColor,
                      )}
                      aria-hidden="true"
                    />
                  ) : (
                    <div className="h-1 w-1 rounded-full bg-zinc-300 ring-3 ring-white" />
                  )}
                </React.Fragment>
              )}
            </div>

            <div className="flex-auto py-0.5 min-w-0">
              <div className="flex justify-between items-start gap-x-2">
                <div className="text-[10px] leading-4 text-zinc-500 min-w-0 break-words w-full">
                  {/* FIX: Use definition.render directly, don't pass to renderEventContent */}
                  {definition.render(event, false)}
                </div>
                <div className="flex items-center gap-1">
                  {pinnedContext && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        pinnedContext.togglePin(event.id, event.event_code);
                      }}
                      className={cn(
                        'p-0.5 rounded transition-colors',
                        isPinned
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-zinc-400 hover:text-zinc-600 opacity-0 group-hover/item:opacity-100',
                      )}
                      title={isPinned ? 'Unpin activity' : 'Pin activity'}
                    >
                      <Pin className="h-3 w-3" />
                    </button>
                  )}
                  <time
                    dateTime={eventTime || ''}
                    className="flex-none text-[10px] leading-4 text-zinc-400 whitespace-nowrap"
                    title={fullTime}
                  >
                    {smartTime}
                  </time>
                </div>
              </div>
              <MatterLink
                matterId={(event as { matter?: string }).matter || ''}
                matterName={
                  (event as { matter_name?: string }).matter_name || ''
                }
                className="mt-0.5"
              />
            </div>
          </>
        )}
      </li>
    );
  },
);

EventItem.displayName = 'EventItem';
